import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  atualizarAnoModeloVeiculo,
  atualizarModeloVeiculo,
  buscarAnosModelo,
  buscarVeiculoPorChassi,
  deletarVeiculo,
} from "../../api/veiculosApi";
import { AnoModelo, Modelo, Veiculo } from "../../types/veiculo";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Veiculos: React.FC = () => {
  const [buscandoVeiculo, setBuscandoVeiculo] = useState<boolean>(false);
  const [chassi, setChassi] = useState<string>("");
  const [veiculo, setVeiculo] = useState<Veiculo | null>(null);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [modoEdicao, setModoEdicao] = useState<boolean>(false);
  const [modeloSelecionado, setModeloSelecionado] = useState<Modelo | null>(
    null
  );
  const [anosModelo, setAnosModelo] = useState<AnoModelo[]>([]);
  const [anoModeloSelecionado, setAnoModeloSelecionado] =
    useState<AnoModelo | null>(null);
  const [modoEdicaoAno, setModoEdicaoAno] = useState<boolean>(false);
  const [carregandoAnos, setCarregandoAnos] = useState<boolean>(false);
  const [salvandoAno, setSalvandoAno] = useState<boolean>(false);
  const [abrirDialogExcluir, setAbrirDialogExcluir] = useState<boolean>(false);
  const [excluindo, setExcluindo] = useState<boolean>(false);

  const handlePesquisar = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.warn("Token não encontrado");
        return;
      }

      setBuscandoVeiculo(true);

      const resultado = await buscarVeiculoPorChassi(token, chassi);
      setVeiculo(resultado.veiculo);
      setModelos(resultado.modelos);
      setModeloSelecionado(
        resultado.modelos.find((m) => m.id === resultado.veiculo.id_modelo) ||
          null
      );

      toast.success("Veículo encontrado com sucesso");
    } catch (error) {
      console.error("Erro na busca:", error);
      toast.error("Erro ao buscar veículo");
      setVeiculo(null);
      setModelos([]);
    } finally {
      setBuscandoVeiculo(false);
    }
  };

  const handleSalvarModelo = async () => {
    if (!veiculo || !modeloSelecionado) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.warn("Token não encontrado");
        return;
      }

      await atualizarModeloVeiculo(token, veiculo.id, modeloSelecionado.id);

      toast.success("Modelo atualizado com sucesso!");
      setModoEdicao(false);
    } catch (error) {
      console.error("Erro ao atualizar modelo:", error);
      toast.error("Erro ao atualizar modelo");
    }
  };

  const handleAbrirAlterarAno = async () => {
    if (!veiculo) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.warn("Token não encontrado");
      return;
    }

    try {
      setCarregandoAnos(true);
      const lista = await buscarAnosModelo(token);
      setAnosModelo(lista);

      setModoEdicaoAno(true);
    } catch (error) {
      console.error("Erro ao buscar anos/modelo:", error);
      toast.error("Erro ao buscar anos/modelo");
    } finally {
      setCarregandoAnos(false);
    }
  };

  const handleSalvarAnoModelo = async () => {
    if (!veiculo || !anoModeloSelecionado) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.warn("Token não encontrado");
      return;
    }

    try {
      setSalvandoAno(true);
      await atualizarAnoModeloVeiculo(
        token,
        veiculo.id,
        anoModeloSelecionado.id
      );

      toast.success("Ano/modelo atualizado com sucesso!");

      setModoEdicaoAno(false);
    } catch (error) {
      console.error("Erro ao atualizar ano/modelo:", error);
      toast.error("Erro ao atualizar ano/modelo");
    } finally {
      setSalvandoAno(false);
    }
  };

  const handleAbrirDialogExcluir = () => {
    setAbrirDialogExcluir(true);
  };

  const handleFecharDialogExcluir = () => {
    if (!excluindo) {
      setAbrirDialogExcluir(false);
    }
  };

  const handleExcluirVeiculo = async () => {
    if (!veiculo) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.warn("Token não encontrado");
      return;
    }

    try {
      setExcluindo(true);

      await deletarVeiculo(token, veiculo.id);

      toast.success("Veículo excluído com sucesso!");

      setVeiculo(null);
      setModelos([]);
      setModoEdicao(false);
      setModoEdicaoAno(false);
      setAnoModeloSelecionado(null);
      setModeloSelecionado(null);
    } catch (error) {
      console.error("Erro ao excluir veículo:", error);
      toast.error("Erro ao excluir veículo");
    } finally {
      setExcluindo(false);
      setAbrirDialogExcluir(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePesquisar();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Busque o Veículo
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          label="Informe o chassi"
          variant="outlined"
          fullWidth
          disabled={buscandoVeiculo}
          value={chassi}
          onChange={(e) => setChassi(e.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{ htmlInput: { maxLength: 17 } }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handlePesquisar}
          disabled={buscandoVeiculo || chassi.length === 0}
        >
          {buscandoVeiculo ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Buscando...
            </>
          ) : (
            "Pesquisar"
          )}
        </Button>
      </Box>

      {veiculo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detalhes do Veículo
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography>
              <strong>ID:</strong> {veiculo.id}
            </Typography>
            <Typography>
              <strong>Chassi:</strong> {veiculo.chassi_veiculo}
            </Typography>
            <Typography>
              <strong>Modelo:</strong> {veiculo.descricao_modelo}
            </Typography>
            <Typography>
              <strong>ID da Empresa:</strong> {veiculo.id_empresa}
            </Typography>
            <Typography>
              <strong>ID do Modelo:</strong> {veiculo.id_modelo}
            </Typography>
          </CardContent>
        </Card>
      )}

      {veiculo && (
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Button variant="outlined" onClick={() => setModoEdicao(true)}>
            Alterar Modelo
          </Button>

          <Button variant="outlined" onClick={handleAbrirAlterarAno}>
            Alterar ano modelo
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={handleAbrirDialogExcluir}
          >
            Excluir veículo
          </Button>
        </Box>
      )}

      {modoEdicao && (
        <Box sx={{ mt: 2, maxWidth: 400 }}>
          <Autocomplete
            fullWidth
            options={modelos}
            getOptionLabel={(option) => option.descricao_modelo}
            value={modeloSelecionado}
            onChange={(_, newValue) => setModeloSelecionado(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione o novo modelo"
                variant="outlined"
              />
            )}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={handleSalvarModelo}>
              Salvar
            </Button>
            <Button variant="outlined" onClick={() => setModoEdicao(false)}>
              Cancelar
            </Button>
          </Box>
        </Box>
      )}
      {modoEdicaoAno && (
        <Box sx={{ mt: 2, maxWidth: 400 }}>
          <Autocomplete<AnoModelo>
            fullWidth
            options={anosModelo}
            loading={carregandoAnos}
            getOptionLabel={(option) => option.descricao_ano_modelo}
            value={anoModeloSelecionado}
            onChange={(_, newValue) => setAnoModeloSelecionado(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione o novo ano/modelo"
                variant="outlined"
              />
            )}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSalvarAnoModelo}
              disabled={!anoModeloSelecionado || salvandoAno}
            >
              {salvandoAno ? "Salvando..." : "Salvar ano/modelo"}
            </Button>
            <Button variant="outlined" onClick={() => setModoEdicaoAno(false)}>
              Cancelar
            </Button>
          </Box>
        </Box>
      )}
      <Dialog
        open={abrirDialogExcluir}
        onClose={handleFecharDialogExcluir}
        aria-labelledby="dialog-excluir-veiculo-titulo"
      >
        <DialogTitle id="dialog-excluir-veiculo-titulo">
          Excluir veículo
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este veículo?
            <br />
            <br />
            Essa ação não poderá ser desfeita.
          </DialogContentText>
          {veiculo && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>ID:</strong> {veiculo.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Chassi:</strong> {veiculo.chassi_veiculo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Modelo:</strong> {veiculo.descricao_modelo}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDialogExcluir} disabled={excluindo}>
            Cancelar
          </Button>
          <Button
            onClick={handleExcluirVeiculo}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Veiculos;
